import type {
  AssignedPatientResponse,
  InterventionGroupDto,
  TreatmentPlanDto,
  YearPlanResponse
} from '../api/generated';
import { ECONOMICS_KEY_SEPARATOR } from '../constants/economicsConstants';
import type {
  EconomicsChangeEntry,
  EconomicsGroupNode,
  EconomicsLeafKey,
  EconomicsLeafState,
  EconomicsPatientNode,
  EconomicsPlanNode,
  EconomicsTreeModel,
  SelectionState
} from '../types/economicsTypes';

export function serializeKey(key: EconomicsLeafKey): string {
  return [key.treatmentPlanId, key.interventionGroupId, key.patientId].join(ECONOMICS_KEY_SEPARATOR);
}

function parseKey(serialized: string): EconomicsLeafKey {
  const [treatmentPlanId, interventionGroupId, patientId] = serialized.split(ECONOMICS_KEY_SEPARATOR);
  return { treatmentPlanId, interventionGroupId, patientId };
}

// Shadow seats never appear in economics — only real patients are billable.
function realSeats(group: InterventionGroupDto): AssignedPatientResponse[] {
  return (group.assignedPatients ?? []).filter((p) => !p.shadow);
}

function collectGroupLeafStates(
  planId: string,
  group: InterventionGroupDto,
  states: Map<string, EconomicsLeafState>
): void {
  const groupId = group.id;
  if (!groupId) return;
  for (const ap of realSeats(group)) {
    const key = serializeKey({
      treatmentPlanId: planId,
      interventionGroupId: groupId,
      patientId: ap.patientId
    });
    states.set(key, {
      completed: ap.completed,
      reimbursementOverride: ap.reimbursementOverride ?? null,
      note: ap.reimbursementNote ?? null
    });
  }
}

// Builds the baseline leaf state from a server year plan: every patient assigned to every
// group across every plan, regardless of week (period filtering happens at render time).
export function buildLeafStates(yearPlan: YearPlanResponse): Map<string, EconomicsLeafState> {
  const states = new Map<string, EconomicsLeafState>();
  for (const plan of yearPlan.treatmentPlans) {
    const planId = plan.id;
    if (!planId) continue;
    for (const week of plan.weeks) {
      for (const group of week.groups ?? []) {
        collectGroupLeafStates(planId, group, states);
      }
    }
  }
  return states;
}

function selectionFrom(completedFlags: boolean[]): SelectionState {
  if (completedFlags.length === 0 || completedFlags.every((c) => !c)) return 'none';
  if (completedFlags.every((c) => c)) return 'all';
  return 'partial';
}

function collectGroupNodes(
  planId: string,
  group: InterventionGroupDto,
  weekNumber: number,
  working: Map<string, EconomicsLeafState>,
  byPatient: Map<string, EconomicsGroupNode[]>
): void {
  const groupId = group.id;
  if (!groupId) return;
  const defaultAmount = group.reimbursementAmount ?? 0;
  for (const ap of realSeats(group)) {
    const key = serializeKey({
      treatmentPlanId: planId,
      interventionGroupId: groupId,
      patientId: ap.patientId
    });
    const state = working.get(key) ?? {
      completed: ap.completed,
      reimbursementOverride: ap.reimbursementOverride ?? null,
      note: ap.reimbursementNote ?? null
    };
    const node: EconomicsGroupNode = {
      key,
      treatmentPlanId: planId,
      interventionGroupId: groupId,
      patientId: ap.patientId,
      groupName: group.name ?? '',
      weekNumber,
      defaultAmount,
      completed: state.completed,
      reimbursementOverride: state.reimbursementOverride,
      note: state.note,
      effectiveAmount: state.reimbursementOverride ?? defaultAmount,
      isOverridden: state.reimbursementOverride !== null,
      completedAt: ap.completedAt ?? null
    };
    const list = byPatient.get(ap.patientId) ?? [];
    list.push(node);
    byPatient.set(ap.patientId, list);
  }
}

function collectPlanGroupNodes(
  plan: TreatmentPlanDto,
  planId: string,
  working: Map<string, EconomicsLeafState>,
  fromWeek: number,
  toWeek: number
): Map<string, EconomicsGroupNode[]> {
  const byPatient = new Map<string, EconomicsGroupNode[]>();
  for (const week of plan.weeks) {
    const weekNumber = week.weekNumber ?? 0;
    if (weekNumber < fromWeek || weekNumber > toWeek) continue;
    for (const group of week.groups ?? []) {
      collectGroupNodes(planId, group, weekNumber, working, byPatient);
    }
  }
  return byPatient;
}

function buildPatientNodes(
  byPatient: Map<string, EconomicsGroupNode[]>,
  planId: string,
  getPatientName: (patientId: string) => string
): EconomicsPatientNode[] {
  const patientNodes: EconomicsPatientNode[] = [];
  for (const [patientId, groups] of byPatient) {
    groups.sort((a, b) => a.weekNumber - b.weekNumber || a.groupName.localeCompare(b.groupName));
    patientNodes.push({
      patientId,
      treatmentPlanId: planId,
      patientName: getPatientName(patientId),
      groups,
      completedCount: groups.filter((g) => g.completed).length,
      total: groups.reduce((sum, g) => sum + (g.completed ? g.effectiveAmount : 0), 0),
      selectionState: selectionFrom(groups.map((g) => g.completed))
    });
  }
  patientNodes.sort((a, b) => a.patientName.localeCompare(b.patientName));
  return patientNodes;
}

// Pivots the server data into a Plan -> Patient -> Group tree, filtered to [fromWeek, toWeek],
// using the local working state for completion/override. Only actionable rows are kept.
export function buildTree(
  yearPlan: YearPlanResponse,
  working: Map<string, EconomicsLeafState>,
  fromWeek: number,
  toWeek: number,
  getPatientName: (patientId: string) => string
): EconomicsTreeModel {
  const planNodes: EconomicsPlanNode[] = [];
  let grandTotal = 0;
  let grandCompleted = 0;

  for (const plan of yearPlan.treatmentPlans) {
    const planId = plan.id;
    if (!planId) continue;

    const byPatient = collectPlanGroupNodes(plan, planId, working, fromWeek, toWeek);
    if (byPatient.size === 0) continue;

    const patientNodes = buildPatientNodes(byPatient, planId, getPatientName);
    const planTotal = patientNodes.reduce((sum, p) => sum + p.total, 0);
    const planCompleted = patientNodes.reduce((sum, p) => sum + p.completedCount, 0);
    const planFlags = patientNodes.flatMap((p) => p.groups.map((g) => g.completed));
    planNodes.push({
      treatmentPlanId: planId,
      planName: plan.name,
      patients: patientNodes,
      completedCount: planCompleted,
      total: planTotal,
      selectionState: selectionFrom(planFlags)
    });
    grandTotal += planTotal;
    grandCompleted += planCompleted;
  }

  planNodes.sort((a, b) => a.planName.localeCompare(b.planName));
  return { plans: planNodes, grandTotal, completedCount: grandCompleted };
}

// Normalizes a leaf to its billable shape: a not-completed leaf carries no override,
// and without an override there is no amount for a note to explain.
function normalize(state: EconomicsLeafState): EconomicsLeafState {
  const reimbursementOverride = state.completed ? state.reimbursementOverride : null;
  return {
    completed: state.completed,
    reimbursementOverride,
    note: reimbursementOverride === null ? null : state.note
  };
}

// Produces the minimal set of changes between the server snapshot and the working state.
export function diffChanges(
  server: Map<string, EconomicsLeafState>,
  working: Map<string, EconomicsLeafState>
): EconomicsChangeEntry[] {
  const changes: EconomicsChangeEntry[] = [];
  for (const [key, workingState] of working) {
    const serverState = server.get(key);
    const w = normalize(workingState);
    const s = serverState ? normalize(serverState) : { completed: false, reimbursementOverride: null };
    if (w.completed === s.completed && w.reimbursementOverride === s.reimbursementOverride) continue;
    // The note travels with an amount change even when it ends at null: clearing an override back
    // to the group default still has to be explained, and the reason is the user's, not the diff's.
    changes.push({
      ...parseKey(key),
      completed: w.completed,
      reimbursementOverride: w.reimbursementOverride,
      note: noteFor(w, workingState)
    });
  }
  return changes;
}

// A cleared override drops its note in `normalize`, but the backend still demands a reason for the
// clearing itself — so fall back to the note the user typed in the dialog before it was cleared.
function noteFor(normalized: EconomicsLeafState, edited: EconomicsLeafState): string | null {
  return normalized.note ?? edited.note;
}
